import {
  httpBatchLink,
  httpLink,
  splitLink,
  type TRPCLink,
} from "@trpc/client";
import type { AppRouter } from "../server";
import { fetchWithEarlyFetches } from "./earlyFetch";
import { devDebugLog } from "./utils/devDebug";

// This module is a dependency-light runtime leaf (only @trpc/client +
// devDebug/earlyFetch; the AppRouter import is type-only) so the link-routing
// unit tests can import it without dragging in the full client module graph
// (trpc.ts -> ImperativeState -> socket.io/Sentry/engine).

/**
 * Client-only request classification. `requestClass: "critical"` routes an
 * operation through an unbatched `httpLink` so its small, first-paint response
 * is never queued in one HTTP batch behind a large corpus response
 * (getFunderSummaries, getFunderWorkspaceEnrichments, ...) that happened to
 * mount in the same render.
 *
 * This is transport metadata only — it never reaches the server and changes
 * no authorization or server behavior. React Query keys are unaffected, so
 * critical hooks still deduplicate against vanilla route prefetches.
 *
 * Three sanctioned categories, both about the same contention:
 * - Queries that directly gate the first visible media/transcript content
 *   (kept small and unbatched so nothing large can queue ahead of them).
 * - The corpus pair itself (getFunderSummaries/getFunderWorkspaceEnrichments):
 *   unbatched from the other side, so it never holds a batch hostage — and a
 *   solo GET is what lets the server's pre-encoded brotli byte lane answer it
 *   (server/api/corpusBytes.ts matches exact per-procedure GET paths, which a
 *   comma-joined batch URL can never hit).
 * - Round preparedness: its multi-stage status audit must not hold the round
 *   selector or other small navigation queries in the same batch.
 * Marking everything critical would just recreate the contention as many
 * separate requests.
 */
export const CRITICAL_REQUEST_CLASS = "critical";

/**
 * Spread into a `useQuery` options object to classify the call as critical:
 *
 *   trpc.getMeetings.useQuery(input, { ...CRITICAL_TRPC_OPTIONS, enabled });
 */
export const CRITICAL_TRPC_OPTIONS = {
  trpc: { context: { requestClass: CRITICAL_REQUEST_CLASS } },
} as const;

type TrpcFetch = NonNullable<Parameters<typeof httpLink>[0]["fetch"]>;

/**
 * The shared React tRPC client's link stack: a `splitLink` that sends
 * critical-classified operations down an unbatched `httpLink` and everything
 * else down the existing `httpBatchLink`. Both lanes share the same URL and
 * auth/impersonation header builder. `fetch` is injectable for unit tests.
 */
export function createTrpcLinks(options: {
  url: string;
  headers: () => Record<string, string>;
  fetch?: TrpcFetch;
}): TRPCLink<AppRouter>[] {
  // Default both lanes onto the early-fetch-aware fetch so a request whose
  // URL matches an in-flight shell early fetch (earlyFetch.ts) adopts it
  // instead of duplicating it. Batch-lane URLs never match (the shell only
  // fires unbatched GETs) — routing through the same wrapper is just uniform
  // plumbing. Tests still inject their own recorder fetch.
  const { url, headers, fetch = fetchWithEarlyFetches } = options;
  return [
    splitLink<AppRouter>({
      condition: (op) => {
        const isCritical = op.context.requestClass === CRITICAL_REQUEST_CLASS;
        // Dev-only (no-op in production builds): makes request class visible
        // per procedure when interpreting network traces on device.
        devDebugLog("trpc", isCritical ? "critical-lane" : "batch-lane", {
          path: op.path,
        });
        return isCritical;
      },
      true: httpLink({ url, headers, fetch }),
      false: httpBatchLink({
        url,
        headers,
        fetch,
        // Queries that mount together (e.g. the round-boot burst:
        // getCurrentUser/getVideos/getUserVideoState/getImpersonatableUsers)
        // collapse into a single HTTP round-trip instead of one each. Cap the
        // batched GET URL length so a large batch transparently splits rather
        // than overflowing the server's URL limit.
        maxURLLength: 14000,
      }),
    }),
  ];
}
